import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Chip, FormGenerator } from '@microservices/wiskey-react-components'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { CachedOutlined } from '@mui/icons-material'
import { Box, Grid, IconButton } from '@mui/material'
import { GridColDef, GridEventListener, GridRowsProp } from '@mui/x-data-grid'

import { Tab, TabPanel, Tabs } from '@components/Tabs'

import { FLEXIBLE, GENERATOR_INPUT_TYPE } from '@constants'
import { AutocompleteOption, DictionaryCategory } from '@types'

type TableWithFiltersProps = {
  defaultValueSelect?: string
  rows: GridRowsProp
  columns: GridColDef[]
  filterName: string
  filterOptions: DictionaryCategory[]
  placeholderInput?: string
  externalOnSelectChange?: (arg?: any) => void
  onRefresh?: () => void
  loading: boolean
  countPage: number
  handleLoadMore: GridEventListener<'rowsScrollEnd'>
}

type FormValues = {
  selectCat: AutocompleteOption
}

export const TableWithFilters: FC<TableWithFiltersProps> = ({
  rows,
  columns,
  filterName,
  filterOptions,
  externalOnSelectChange,
  onRefresh,
  loading,
  handleLoadMore,
  countPage,
}) => {
  const { t } = useTranslation()
  const methods = useForm<FormValues>({
    defaultValues: { selectCat: { id: 0, label: '' } },
  })
  const { watch, reset } = methods
  const watchSelect = watch('selectCat')

  useEffect(() => {
    onChangeSelect(watchSelect)
  }, [watchSelect])

  const onChangeSelect = (value: AutocompleteOption): void => {
    externalOnSelectChange?.(filterOptions.find(option => option?.id === value?.id)?.code)
  }

  const handleRefresh = (): void => {
    onRefresh?.()
    reset()
  }

  return (
    <Box
      sx={{
        ...FLEXIBLE,
        width: '100%',
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={0}>
          <Tab label={t('tableWithFilters.title')} />
        </Tabs>
      </Box>
      <TabPanel contentSx={FLEXIBLE} index={0} sx={FLEXIBLE} value={0}>
        <Grid container spacing={3}>
          <Grid container item alignItems='start' sm={12} spacing={1} xs={12}>
            <Grid item>
              <Chip label={filterName} sx={{ height: '40px' }} />
            </Grid>
            <Grid item flexGrow={1} ml={1}>
              <FormProvider {...methods}>
                <form>
                  <FormGenerator
                    inputs={[
                      {
                        name: 'selectCat',
                        inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                        autocompleteOptions: filterOptions.map(op => ({
                          id: Number(op.id),
                          label: op.title,
                        })),
                        size: 'small',
                        placeholder: t('placeholder.category'),
                      },
                    ]}
                  />
                </form>
              </FormProvider>
            </Grid>
            <Grid item>
              <IconButton onClick={handleRefresh}>
                <CachedOutlined />
              </IconButton>
            </Grid>
          </Grid>
          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size='small'
              placeholder={placeholderInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid> */}
        </Grid>
        <DataGrid
          hideFooter
          columns={columns}
          fetchNextPage={handleLoadMore}
          loading={loading}
          pageCount={countPage}
          paginationModel={{ page: 0, pageSize: 10 }}
          rows={rows}
          sx={{ borderRadius: 0 }}
        />
      </TabPanel>
    </Box>
  )
}
